import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { HTMLAttributes, MouseEventHandler, ReactNode } from 'react';

import type { Elevation } from 'storybookConfig/mixins/elevate';
import elevate from 'storybookConfig/mixins/elevate';
import resetButtonStyles from 'storybookConfig/mixins/resetButtonStyles';
import typography from 'storybookConfig/mixins/typography';
import type { IconName } from 'storybookConfig/mixins/icons';
import Icon from 'storybookConfig/stories/molecules/Icon';
import type { ThemeColor } from 'storybookConfig/stories/theme';
import { breakpointValues } from 'storybookConfig/stories/theme';

interface AlertProps {
  /* The type of alert */
  type: 'error' | 'success' | 'warning' | 'info';
  /* The alert's title */
  title?: string;
  /* The body of the alert message */
  children: ReactNode;
  /*
   * Whether or not to display the UI with a drop shadow.
   * Helpful for integration with the AlertQueue component.
   */
  hasDropShadow?: boolean;
  /**
   * Elevation key
   */
  elevation?: Elevation;
  /* Adding this handler will add a Close button to the UI */
  onClose?: MouseEventHandler<HTMLButtonElement>;
  /* Lines of body shown before it is truncated. Inline alerts show it all; toasts clamp to 3 */
  bodyLineClamp?: number | 'none';
  /* A button or link for the alert's next step, at the end of the row (under the text on mobile) */
  action?: ReactNode;
  /* The title's element. Pass a heading level when the alert heads a section of the page */
  titleAs?: 'h2' | 'h3' | 'h4';
  /* Outer margin, e.g. `0 0 16px`, for call sites whose parent cannot space it with a gap */
  margin?: string;
}

interface WrapperProps {
  type: 'error' | 'success' | 'warning' | 'info';
  hasDropShadow?: boolean;
  elevation?: Elevation;
  $margin?: string;
}

const MOBILE = `@media screen and (max-width: ${breakpointValues.sm - 1}px)`;

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  align-items: stretch;
  background-color: ${({ theme }) => theme.color.surfaceCanvas};
  /* A hairline card with a 4px edge in the status colour. */
  border: 1px solid ${({ theme }) => theme.color.border};
  border-left-width: 4px;
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  ${({ $margin }) =>
    $margin &&
    css`
      margin: ${$margin};
    `}

  ${MOBILE} {
    flex-wrap: wrap;
  }

  ${({ hasDropShadow }) =>
    hasDropShadow &&
    css`
      ${elevate('8')};
    `}

  ${({ elevation }) => elevation && elevate(elevation)}

  ${({ type, theme }) => {
    if (type === 'error')
      return css`
        border-left-color: ${theme.color.errorBase};
      `;

    if (type === 'success')
      return css`
        border-left-color: ${theme.color.successBase};
      `;

    if (type === 'warning')
      return css`
        border-left-color: ${theme.color.warningBase};
      `;

    if (type === 'info')
      return css`
        border-left-color: ${theme.color.infoBase};
      `;

    return css``;
  }};
`;

const IconWrapper = styled.div`
  align-self: center;
  line-height: 1;
  width: 64px;
  min-width: 64px;
`;

export const ContentWrapper = styled.div`
  flex-grow: 1;
  padding: 16px 16px 16px 0;
`;

type AlertType = AlertProps['type'];

const ICONS: Record<AlertType, { name: IconName; color: ThemeColor; tint: ThemeColor }> = {
  error: { name: 'circle-alert', color: 'errorBase', tint: 'errorTint' },
  success: { name: 'circle-check', color: 'successStrong', tint: 'successTint' },
  warning: { name: 'triangle-alert', color: 'warningStrong', tint: 'warningTint' },
  info: { name: 'info', color: 'infoBase', tint: 'infoTint' },
};

/* The status icon in a tinted circle; one shape for every type. */
const IconCircle = styled.span<{ type: AlertType }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  margin: 0 auto;
  border-radius: 100%;
  background: ${({ theme, type }) => theme.color[ICONS[type].tint]};
`;

const Title = styled.strong`
  display: block;
  color: ${({ theme }) => theme.color.bodyTextPrimary};
  margin: 0 0 4px;
  ${() => typography('Headings/H4')};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Body = styled('div', {
  shouldForwardProp: (prop) => prop !== 'lineClamp',
})<{ lineClamp: number | 'none' }>`
  color: ${({ theme }) => theme.color.bodyTextSecondary};
  ${() => typography('Body/Body Small')};
  white-space: pre-line;
  display: -webkit-box;
  -webkit-line-clamp: ${({ lineClamp }) => lineClamp};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Action = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 16px 24px 16px 0;

  /* Under the text, past the icon column, so a narrow screen squeezes neither. */
  ${MOBILE} {
    order: 1;
    flex-basis: 100%;
    padding: 0 16px 16px 64px;
  }
`;

const CloseButton = styled.button`
  ${resetButtonStyles};
  justify-self: stretch;
  cursor: pointer;
  color: ${({ theme }) => theme.color.bodyTextSecondary};
  border-left: 1px solid ${({ theme }) => theme.color.border};
  padding: 24px;
  ${() => typography('Buttons/Button Small')};
  width: 88px;
  &:hover {
    background: ${({ theme }) => theme.color.surfaceSubtle};
  }
`;

const Alert = ({
  type,
  title,
  children,
  hasDropShadow,
  onClose,
  bodyLineClamp = 'none',
  action,
  titleAs,
  margin,
  ...wrapperProps
}: AlertProps & HTMLAttributes<HTMLDivElement>) => {
  return (
    <Wrapper type={type} hasDropShadow={hasDropShadow} $margin={margin} {...wrapperProps}>
      <IconWrapper>
        <IconCircle type={type}>
          <Icon name={ICONS[type].name} color={ICONS[type].color} size="20px" />
        </IconCircle>
      </IconWrapper>

      <ContentWrapper>
        {title && <Title as={titleAs}>{title}</Title>}
        <Body lineClamp={bodyLineClamp}>{children}</Body>
      </ContentWrapper>

      {action && <Action>{action}</Action>}

      {/* type="button": inside a form, a bare button submits it. */}
      {onClose && (
        <CloseButton type="button" onClick={onClose}>
          Close
        </CloseButton>
      )}
    </Wrapper>
  );
};

export default Alert;
